import React, { useState } from 'react';
import {
  Typography,
  Box,
  Button,
  Stack,
  IconButton,
  Paper,
  Fade,
  useMediaQuery,
  useTheme
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import {
  ArrowUpward,
  Close,
  Minimize,
  PictureInPictureAlt,
  OpenInNew
} from '@mui/icons-material';
import DensitySmallIcon from '@mui/icons-material/DensitySmall';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import NorthWestIcon from '@mui/icons-material/NorthWest';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import SouthWestIcon from '@mui/icons-material/SouthWest';
import SouthEastIcon from '@mui/icons-material/SouthEast';
import { useAppSelector, useAppDispatch } from 'src/app/hooks';
import {
  togglePictureInPictureMode,
  setPipPosition,
  pipSize,
  pipPositionMargin,
  initialState
} from 'src/features/chatNow';
import { useNavigate } from 'react-router-dom';
import { CHAT_NOW_NEW_CHAT } from 'src/route';
import TopicCard from 'src/content/chatNow/components/TopicCard';
import ChatList from 'src/content/chatNow/components/ChatList';
import { useChatLogic } from 'src/hooks/useChat';

interface ChatComponentProps {
  mode: 'normal' | 'pip';
  isVisible?: boolean;
}

// PiP corner positions
const TOP_LEFT = { x: 0 + pipPositionMargin, y: 0 + pipPositionMargin };
const TOP_RIGHT = {
  x: window.innerWidth - (pipSize.width + pipPositionMargin),
  y: 0 + pipPositionMargin
};
const BOTTOM_LEFT = {
  x: 0 + pipPositionMargin,
  y: window.innerHeight - (pipSize.height + pipPositionMargin)
};
const BOTTOM_RIGHT = initialState.pipPosition;

const ChatNow: React.FC<ChatComponentProps> = ({ mode, isVisible = true }) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    pipPosition,
    pipSize,
    conversation: chatConversation,
    chatMode,
    booleanConversation
  } = useAppSelector((state) => state.chatNow);

  const {
    chatInputValue,
    isLoading,
    sendingLoading,
    handleSendMessage,
    handleChange,
    handleClickTopic
  } = useChatLogic();

  const conversation =
    chatMode === 'chatNow' ? chatConversation : booleanConversation;

  // PiP specific state
  const [isMinimized, setIsMinimized] = useState(false);

  const handleTogglePiP = () => {
    dispatch(togglePictureInPictureMode());
    if (mode === 'pip') {
      navigate(CHAT_NOW_NEW_CHAT);
    }
  };

  const handleClose = () => {
    dispatch(togglePictureInPictureMode());
  };

  const handleMinimize = () => {
    setIsMinimized(!isMinimized);
    if (!isMinimized === true)
      dispatch(
        setPipPosition({
          ...BOTTOM_RIGHT,
          y:
            window.innerHeight -
            (initialState.pipPosition.y + pipPositionMargin + 40)
        })
      );
    else dispatch(setPipPosition(BOTTOM_RIGHT));
  };

  const matchedMediaUpMd = useMediaQuery(theme.breakpoints.up('md'));

  const renderEmptyChat = () => (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      flexGrow={1}
      gap={mode === 'pip' ? 1 : 2}
      sx={{ padding: mode === 'pip' ? 2 : 0 }}
    >
      <Typography
        variant="body2"
        fontSize={mode === 'pip' ? 14 : 20}
        textAlign="center"
      >
        How may I assist you?{' '}
        <AutoFixHighIcon fontSize="small" sx={{ color: '#0B85AE' }} />
      </Typography>
      <Stack
        gap={1}
        sx={{
          maxWidth: mode === 'pip' ? '250px' : '298px',
          marginBottom: mode === 'pip' ? 0 : '50px'
        }}
      >
        <TopicCard
          description="Chat Now"
          icon={
            <LibraryBooksIcon
              sx={{
                fontSize: mode === 'pip' ? '14px' : '16px',
                color: '#4B4B4B'
              }}
            />
          }
          onClick={() => handleClickTopic('chatNow')}
        />
        <TopicCard
          description="Generate boolean query"
          icon={
            <DensitySmallIcon
              sx={{
                fontSize: mode === 'pip' ? '14px' : '16px',
                color: '#4B4B4B'
              }}
            />
          }
          onClick={() => handleClickTopic('booleanQuery')}
        />
      </Stack>
    </Box>
  );

  const renderChatInput = () => (
    <Box
      sx={{
        width: '100%',
        maxWidth: mode === 'pip' ? '100%' : '800px',
        padding: mode === 'pip' ? 2 : 0,
        paddingBottom: mode === 'pip' ? 2 : '50px',
        borderTop: mode === 'pip' ? '1px solid #e0e0e0' : 'none'
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <Box
          component="input"
          sx={{
            width: '100%',
            padding:
              mode === 'pip' ? '8px 40px 8px 12px' : '16px 40px 16px 16px',
            borderRadius: mode === 'pip' ? '20px' : '8px',
            border: '1px solid #ddd',
            outline: 'none',
            fontSize: mode === 'pip' ? '14px' : '16px'
          }}
          value={chatInputValue}
          onChange={handleChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !isLoading) {
              handleSendMessage(chatInputValue);
            }
          }}
          placeholder="Type your message..."
        />
        <Button
          sx={{
            position: 'absolute',
            right: mode === 'pip' ? '4px' : '8px',
            borderRadius: mode === 'pip' ? '50%' : '8px',
            width: mode === 'pip' ? '32px' : '30px',
            height: mode === 'pip' ? '32px' : '30px',
            minWidth: 0,
            padding: 0
          }}
          disabled={chatInputValue.length <= 0 || isLoading}
          onClick={() => handleSendMessage(chatInputValue)}
          variant="contained"
        >
          <ArrowUpward sx={{ color: '#fff' }} fontSize="small" />
        </Button>
      </Box>
    </Box>
  );

  if (mode === 'pip') {
    if (!isVisible) return null;

    return (
      <Fade in={isVisible} timeout={300}>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            left: pipPosition.x,
            top: pipPosition.y,
            width: pipSize.width,
            height: isMinimized ? 60 : pipSize.height,
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '12px',
            overflow: 'hidden',
            border: '1px solid #e0e0e0'
          }}
        >
          {/* PiP Header */}
          <Box
            sx={{
              backgroundColor: '#f5f5f5',
              padding: '8px 12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid #e0e0e0'
            }}
          >
            <IconButton
              size="small"
              onClick={handleTogglePiP}
              sx={{ width: 24, height: 24 }}
            >
              <OpenInNew fontSize="small" sx={{ rotate: '270deg' }} />
            </IconButton>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Chat Now
            </Typography>
            <Box display="flex" gap={0.5}>
              <IconButton
                size="small"
                onClick={handleMinimize}
                sx={{ width: 24, height: 24 }}
              >
                <Minimize fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleClose}
                sx={{ width: 24, height: 24 }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* PiP Content */}
          {!isMinimized && (
            <Box
              sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
            >
              {!chatMode ? (
                renderEmptyChat()
              ) : (
                <ChatList
                  conversation={conversation}
                  isLoading={sendingLoading}
                  handleSendMessage={handleSendMessage}
                />
              )}

              {chatMode !== null && renderChatInput()}
            </Box>
          )}
          {!isMinimized ? (
            <Stack
              direction="row"
              justifyContent="center"
              gap={1}
              paddingBottom={1}
            >
              <IconButton
                sx={{ border: '1px solid #D9D9D9', borderRadius: '10px' }}
                onClick={() => dispatch(setPipPosition(TOP_LEFT))}
                title="Top Left"
              >
                <NorthWestIcon sx={{ fontSize: '20px' }} />
              </IconButton>
              <IconButton
                sx={{ border: '1px solid #D9D9D9', borderRadius: '10px' }}
                onClick={() => dispatch(setPipPosition(TOP_RIGHT))}
                title="Top Right"
              >
                <NorthEastIcon sx={{ fontSize: '20px' }} />
              </IconButton>
              <IconButton
                sx={{ border: '1px solid #D9D9D9', borderRadius: '10px' }}
                onClick={() => dispatch(setPipPosition(BOTTOM_LEFT))}
                title="Bottom Left"
              >
                <SouthWestIcon sx={{ fontSize: '20px' }} />
              </IconButton>
              <IconButton
                sx={{ border: '1px solid #D9D9D9', borderRadius: '10px' }}
                onClick={() => dispatch(setPipPosition(BOTTOM_RIGHT))}
                title="Bottom Right"
              >
                <SouthEastIcon sx={{ fontSize: '20px' }} />
              </IconButton>
            </Stack>
          ) : null}
        </Paper>
      </Fade>
    );
  }

  // Normal mode
  return (
    <Box
      width="100%"
      display="flex"
      flexDirection="column"
      justifyContent="start"
      alignItems="center"
      gap={4}
      sx={{ height: 'calc(100vh - 230px)', position: 'relative' }}
    >
      {matchedMediaUpMd && (
        <IconButton
          sx={{
            display: { xs: 'hidden', md: 'block' },
            position: 'absolute',
            top: '10px',
            right: '10px'
          }}
          onClick={handleTogglePiP}
          title="Open in Picture-in-Picture mode"
        >
          <PictureInPictureAlt />
        </IconButton>
      )}

      {!chatMode ? (
        renderEmptyChat()
      ) : (
        <ChatList
          conversation={conversation}
          isLoading={sendingLoading}
          handleSendMessage={handleSendMessage}
        />
      )}

      {chatMode !== null && renderChatInput()}
    </Box>
  );
};

export default ChatNow;
